import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useEventEditQuery,
  useUpdateEvent,
} from "../api/queries";
import { detectEventFeaturePreset } from "../lib/event-features-ui";
import { EventForm } from "../ui/EventForm";

export function EventEditPage() {
  const { id } = useParams({ from: "/app/performance/events/$id/edit" });
  const nav = useNavigate({ from: "/app/performance/events/$id/edit" });
  const { data: event, isLoading } = useEventEditQuery(id);
  const updateEvent = useUpdateEvent(id);

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ງານສະແດງ", to: "/app/performance/events" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫລດ...</p>
        </Main>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ງານສະແດງ", to: "/app/performance/events" },
              { label: "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບງານສະແດງ</p>
        </Main>
      </>
    );
  }

  const eventFeatures = event.eventFeatures;
  const featurePreset = detectEventFeaturePreset(eventFeatures);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            {
              label: event.title,
              to: "/app/performance/events/$id",
              params: { id },
            },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂງານສະແດງ</h2>
          <p className="text-muted-foreground">{event.title}</p>
          {(event.status === "draft" || event.status === "confirmed") && (
            <p className="mt-2 text-amber-800 text-sm dark:text-amber-200">
              ຖ້າຕ້ອງເລື່ອນວັນສະແດງ ແນະນຳໃຊ້ປຸ່ມ「ເລື່ອນງານ」ໃນໜ້າລາຍລະອຽດ
              ເພື່ອບັນທຶກເຫດຜົນ ແລະ ປະຫວັດ
            </p>
          )}
        </div>

        <div className="rounded-xl border bg-card px-6 pt-6">
          <EventForm
            excludeEventId={id}
            excludeRequestId={event.requestId ?? undefined}
            lockedFeatures={event.lockedFeatures}
            initialValues={{
              title: event.title,
              status: event.status,
              categoryId: event.categoryId ?? "",
              featurePreset,
              eventFeatures,
              eventDate: toDateTimeLocalValue(event.eventDate),
              endDate: toDateTimeLocalValue(event.endDate) || null,
              locationText: event.locationText ?? null,
              locationProvince: event.locationProvince ?? null,
              isProvincial: event.isProvincial ?? false,
              perDiemPolicyId: event.perDiemPolicyId ?? null,
              perDiemDays: event.perDiemDays ?? 1,
              notes: event.notes ?? null,
            }}
            onSubmit={async (vals) => {
              try {
                await updateEvent.mutateAsync({
                  title: vals.title,
                  status: vals.status,
                  categoryId: vals.categoryId,
                  eventFeatures: vals.eventFeatures,
                  eventDate: vals.eventDate,
                  endDate: vals.endDate,
                  locationText: vals.locationText,
                  locationProvince: vals.locationProvince,
                  isProvincial: vals.isProvincial,
                  perDiemPolicyId: vals.perDiemPolicyId,
                  perDiemDays: vals.perDiemDays,
                  notes: vals.notes,
                });
                nav({ to: "/app/performance/events/$id", params: { id } });
              } catch {}
            }}
            onCancel={() =>
              nav({ to: "/app/performance/events/$id", params: { id } })
            }
            submitting={updateEvent.isPending}
          />
        </div>
      </Main>
    </>
  );
}
